<template>
  <div class="settings-panel">
    <!-- No selection -->
    <div v-if="!selectedElement" class="empty-state">
      <div class="empty-state-icon">⚙️</div>
      <div class="empty-state-title">No Selection</div>
      <div class="empty-state-text">
        Select an element, column, or row to edit its settings
      </div>
    </div>

    <!-- Element selected -->
    <div v-else>
      <div class="settings-panel-header">
        <h2>{{ getElementTitle() }}</h2>
        <div class="settings-panel-type">{{ selectedElement.type || 'Structure' }}</div>
      </div>

      <!-- Tabs -->
      <div class="settings-tabs">
        <button 
          v-for="tab in availableTabs" 
          :key="tab"
          :class="['settings-tab', { active: currentTab === tab }]"
          @click="currentTab = tab"
        >
          {{ tab }}
        </button>
      </div>

      <!-- Tab Content -->
      <div class="settings-form">
        <!-- Element Settings -->
        <div v-if="currentTab === 'Element' && selectedElement.type">
          <component 
            :is="getSettingsComponent()" 
            :settings="selectedElement.settings"
            @update="updateElementSettings"
          />
        </div>

        <!-- Style Settings -->
        <div v-if="currentTab === 'Style'">
          <StyleSettings 
            :styles="selectedElement.styles || {}"
            @update="updateStyles"
          />
        </div>

        <!-- Advanced Settings -->
        <div v-if="currentTab === 'Advanced'">
          <AdvancedSettings 
            :element="selectedElement"
            @update="updateAdvanced"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useStructureApi } from '../composables/useStructureApi';

// Element-specific settings components
import TextSettings from './settings/TextSettings.vue';
import ImageSettings from './settings/ImageSettings.vue';
import LinksSettings from './settings/LinksSettings.vue';
import SocialSettings from './settings/SocialSettings.vue';
import NewsletterSettings from './settings/NewsletterSettings.vue';
import HtmlSettings from './settings/HtmlSettings.vue';
import DividerSettings from './settings/DividerSettings.vue';
import SpacerSettings from './settings/SpacerSettings.vue';
import StyleSettings from './settings/StyleSettings.vue';
import AdvancedSettings from './settings/AdvancedSettings.vue';

const props = defineProps({
  selectedElement: {
    type: Object,
    default: null
  },
  structure: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(['update-element', 'update-column', 'update-row', 'update-global']);

const currentTab = ref('Element');

const availableTabs = computed(() => {
  if (!props.selectedElement) return [];
  
  const tabs = [];
  
  if (props.selectedElement.type) {
    tabs.push('Element');
  }
  
  tabs.push('Style', 'Advanced');
  
  return tabs;
});

// Watch selectedElement changes
watch(() => props.selectedElement, () => {
  currentTab.value = 'Element';
});

function getElementTitle() {
  if (!props.selectedElement) return '';
  
  const type = props.selectedElement.type;
  if (!type) {
    // Check if it's row or column
    if (props.selectedElement.columns) return 'Row Settings';
    if (props.selectedElement.width !== undefined) return 'Column Settings';
    return 'Settings';
  }
  
  const titles = {
    text: 'Text Element',
    image: 'Image Element',
    links: 'Links Element',
    social: 'Social Icons',
    menu: 'Menu Element',
    newsletter: 'Newsletter Form',
    html: 'Custom HTML',
    divider: 'Divider',
    spacer: 'Spacer'
  };
  
  return titles[type] || 'Element';
}

function getSettingsComponent() {
  const type = props.selectedElement?.type;
  
  const components = {
    text: TextSettings,
    image: ImageSettings,
    links: LinksSettings,
    social: SocialSettings,
    newsletter: NewsletterSettings,
    html: HtmlSettings,
    divider: DividerSettings,
    spacer: SpacerSettings
  };
  
  return components[type] || null;
}

function updateElementSettings(updates) {
  if (!props.selectedElement) return;
  emit('update-element', props.selectedElement.id, updates);
}

function updateStyles(updates) {
  if (!props.selectedElement) return;
  
  // Determine what type of element this is
  if (props.selectedElement.columns) {
    emit('update-row', props.selectedElement.id, { styles: updates });
  } else if (props.selectedElement.width !== undefined) {
    emit('update-column', props.selectedElement.id, { styles: updates });
  } else if (props.selectedElement.type) {
    emit('update-element', props.selectedElement.id, { styles: updates });
  }
}

function updateAdvanced(updates) {
  if (!props.selectedElement) return;
  emit('update-element', props.selectedElement.id, updates);
}
</script>

